import { EndpointAppBuilder } from "core/src/store/store.types";
import { FileCompetitorUploaderProcessor, FileCompetitorUploaderStatus, Processor, EtlApiTarget, HttpVerb } from "etl/types";

export enum TriggerFiltersTagsEnum {
    GENERAL = "General",
    FILE_COMPETITOR_UPLOADER = "FileCompetitorUploader",
    FILE_COMPETITOR_UPLOADED_STATUS = "FileCompetitorUploadedStatus",
}

export const getTriggerFiltersEndpoints = (builder: EndpointAppBuilder) => ({
    getFileCompetitorUploader: builder.query<FileCompetitorUploaderProcessor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as FileCompetitorUploaderProcessor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.FILE_COMPETITOR_UPLOADER_API}`, method: HttpVerb.GET });
            return result as { data: FileCompetitorUploaderProcessor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }, { type: TriggerFiltersTagsEnum.FILE_COMPETITOR_UPLOADER, id: uuid }],
    }),
    getFileCompetitorUploadedStatus: builder.query<FileCompetitorUploaderStatus, { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: {} as FileCompetitorUploaderStatus };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.FILE_COMPETITOR_UPLOADED_STATUS_API}`, method: HttpVerb.GET });
            return result as { data: FileCompetitorUploaderStatus } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }, { type: TriggerFiltersTagsEnum.FILE_COMPETITOR_UPLOADED_STATUS, id: uuid }],
    }),
    getCsvUnzipper: builder.query<Processor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as Processor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.CSV_UNZIPPER_API}`, method: HttpVerb.GET });
            return result as { data: Processor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }],
    }),
    getFileUnzipper: builder.query<Processor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as Processor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.FILE_UNZIPPER_API}`, method: HttpVerb.GET });
            return result as { data: Processor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }],
    }),
    getCsvPreprocessor: builder.query<Processor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as Processor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.CSV_PREPROCESSOR_API}`, method: HttpVerb.GET });
            return result as { data: Processor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }],
    }),
    getFilesPreprocessor: builder.query<Processor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as Processor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.FILES_PREPROCESSOR_API}`, method: HttpVerb.GET });
            return result as { data: Processor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }],
    }),
    getInputImporter: builder.query<Processor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as Processor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.IMPORTER_CSV_API}`, method: HttpVerb.GET });
            return result as { data: Processor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }],
    }),
    getInputImporterPDF: builder.query<Processor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as Processor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.IMPORTER_PDF_API}`, method: HttpVerb.GET });
            return result as { data: Processor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }],
    }),
    getExtractImporters: builder.query<Processor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as Processor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.EXTRACT_IMPORTERS_API}`, method: HttpVerb.GET });
            return result as { data: Processor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }],
    }),
    getDictionaryGenerators: builder.query<Processor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as Processor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.DICTIONARY_GENERATORS_API}`, method: HttpVerb.GET });
            return result as { data: Processor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }],
    }),
    getOutputCsvGenerators: builder.query<Processor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as Processor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.OUTPUT_CSV_GENERATORS_API}`, method: HttpVerb.GET });
            return result as { data: Processor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }],
    }),
    getOutputDataGenerators: builder.query<Processor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as Processor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.OUTPUT_DATA_GENERATORS_API}`, method: HttpVerb.GET });
            return result as { data: Processor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }],
    }),
    getCsvUploader: builder.query<Processor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as Processor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.CSV_UPLOADER_API}`, method: HttpVerb.GET });
            return result as { data: Processor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }],
    }),
    getFileUploader: builder.query<Processor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as Processor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.FILE_UPLOADER_API}`, method: HttpVerb.GET });
            return result as { data: Processor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }],
    }),
    getOriginalFileUploader: builder.query<Processor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as Processor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.ORIGINAL_FILE_UPLOADER_API}`, method: HttpVerb.GET });
            return result as { data: Processor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }],
    }),
    getAwParser: builder.query<Processor[], { uuid: string }>({
        queryFn: async ({ uuid }, _api, _extraOptions, baseQuery) => {
            if (!uuid) return { data: [] as Processor[] };
            const result = await baseQuery({ url: `${uuid}/${EtlApiTarget.AW_PARSER_API}`, method: HttpVerb.GET });
            return result as { data: Processor[] } | { error: unknown };
        },
        providesTags: (_result, _error, { uuid }) => [{ type: TriggerFiltersTagsEnum.GENERAL, id: uuid }],
    }),
});
